// Home — landing page after Clerk signin. Two flavors:
//
//   • Fresh tenant (zero agents): Welcome card with a 3-step checklist
//     that walks a non-technical operator from signup to a working,
//     embedded assistant in under 2 minutes. The 1-click sample agent
//     does the heavy lifting — no form to fill out.
//
//   • Returning tenant (≥1 agent): an overview dashboard composed of
//     five widgets — KPIs, daily-cost trend, token capacity gauge, top
//     agents, recent activity. The widgets all consume queries that
//     other pages use too (`['usage']`, `['conversations']`,
//     `['agents']`), so navigating to /usage or /conversations after
//     Home is free (warm cache).
//
// Branching at the page level (not inside the cards) keeps the
// returning-user path independent of the welcome-checklist tour.

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertTriangle, Bot, Check, DollarSign, MessagesSquare, Rocket, Sparkles, Wand2 } from 'lucide-react';
import type { UsageSummary } from '@cortex/shared';
import { useApiClient } from '../lib/providers.js';
import { friendlyError } from '../lib/error-message.js';
import { formatUsd } from '../lib/format-money.js';
import { formatDateRange } from '../lib/format-date.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Skeleton } from '../components/ui/skeleton.js';
import { ErrorRetry } from '../components/ui/error-retry.js';
import { PageHeader } from '../components/ui/page-header.js';
import { agentsApi, AGENTS_QUERY_KEY } from '../lib/api/agents.js';
import { usageApi, USAGE_QUERY_KEY } from '../lib/api/usage.js';
import { conversationsApi, CONVERSATIONS_QUERY_KEY } from '../lib/api/conversations.js';
import { KpiCard } from '../components/home/kpi-card.js';
import { CostTrendCard } from '../components/home/cost-trend-card.js';
import { TokenCapacityCard } from '../components/home/token-capacity-card.js';
import { TopAgentsCard } from '../components/home/top-agents-card.js';
import { RecentActivityCard } from '../components/home/recent-activity-card.js';
import { RetentionCard } from '../components/billing/retention-card.js';
import { DisabledResourcesBanner } from '../components/billing/disabled-resources-banner.js';

export function HomePage() {
  const api = useApiClient();
  const agents = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: () => agentsApi.list(api),
  });
  const usage = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });

  // Defensive optional chains: tests stub `get` to return a fixed shape
  // regardless of URL, so `agents.data` may not even have an `agents`
  // array at runtime. The TS type asserts presence, but the runtime
  // reality from naive test stubs doesn't — suppress the lint warning
  // and fall back to `null` (which makes `isFresh` resolve to false, so
  // the returning-tenant view renders without throwing).
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const agentCount: number | null = agents.data?.agents?.length ?? null;
  const isFresh = agentCount === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={isFresh ? 'Welcome to Cortex' : 'Welcome back'}
        description={
          isFresh
            ? "Let's get your first AI assistant live in under two minutes."
            : 'Overview of activity and capacity for the current billing window.'
        }
      />

      {/* Loading: show the dashboard skeleton (matches the returning-user
          layout) so the first paint isn't a content jump. We don't yet
          know if the tenant is fresh — the sample-agent ID is what tips
          us off — so a generic skeleton is the safe default. */}
      {agents.isLoading && <DashboardSkeleton />}

      {!agents.isLoading && isFresh && <WelcomeChecklist />}

      {!agents.isLoading && !isFresh && (
        <>
          {usage.isError && (
            <ErrorRetry
              title="Could not load usage"
              error={usage.error}
              onRetry={() => void usage.refetch()}
            />
          )}
          {usage.data && (
            <RetentionCard summary={usage.data} agentCount={agentCount} />
          )}
          <PlanMismatchBanner count={usage.data?.planMismatchAgentCount ?? 0} />
          <DisabledResourcesBanner
            disabledAgentCount={usage.data?.disabledAgentCount ?? 0}
            excludedKbDocCount={usage.data?.excludedKbDocCount ?? 0}
          />
          <Dashboard
            summary={usage.data ?? null}
            agentCount={agentCount}
          />
        </>
      )}
    </div>
  );
}

// ---------- Returning-tenant overview ------------------------------------

interface DashboardProps {
  summary: UsageSummary | null;
  agentCount: number | null;
}

function Dashboard({ summary, agentCount }: DashboardProps) {
  return (
    <div className="space-y-6">
      <KpiRow summary={summary} agentCount={agentCount} />

      {/* 9+3 split (lg:grid-cols-4 + col-span-3 on chart) keeps the chart
          dominant while aligning its right edge with the 3rd KPI column
          above. The earlier 8+4 split (lg:grid-cols-3 + col-span-2) drifted
          the gauge column out of column register with the KPI row. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="lg:col-span-3">
          {summary ? (
            <CostTrendCard summary={summary} />
          ) : (
            <Card className="h-full">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          )}
        </div>
        <div>
          {summary ? (
            <TokenCapacityCard summary={summary} />
          ) : (
            <Card className="flex h-full flex-col">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent className="flex flex-1 items-center justify-center">
                <Skeleton className="h-28 w-28 rounded-full" />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          {summary ? (
            <TopAgentsCard summary={summary} />
          ) : (
            <Card>
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
        <div>
          <RecentActivityCard />
        </div>
      </div>
    </div>
  );
}

// ---------- KPI row ------------------------------------------------------

interface KpiRowProps {
  summary: UsageSummary | null;
  agentCount: number | null;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function KpiRow({ summary, agentCount }: KpiRowProps) {
  const api = useApiClient();
  // Same queryKey as the RecentActivityCard so we share the underlying
  // fetch — `select` runs per consumer.
  const conversations24h = useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: () => conversationsApi.list(api),
    select: (data) => {
      const cutoff = Date.now() - ONE_DAY_MS;
      return data.conversations.filter(
        (c) => new Date(c.updatedAt).getTime() >= cutoff,
      ).length;
    },
  });

  const runs = summary ? summary.period.runs.toLocaleString() : null;
  const cost = summary ? formatUsd(summary.period.costUsd) : null;
  const periodRange = summary
    ? formatDateRange(summary.plan.currentPeriodStart, summary.plan.currentPeriodEnd)
    : 'this billing period';
  const agentCountLabel =
    agentCount === null ? null : agentCount.toLocaleString();
  const conversations24hLabel = useMemo(() => {
    if (conversations24h.isLoading) return null;
    return (conversations24h.data ?? 0).toLocaleString();
  }, [conversations24h.isLoading, conversations24h.data]);

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label="Runs"
        value={runs}
        sublabel={periodRange}
        icon={Activity}
      />
      <KpiCard
        label="Cost"
        value={cost}
        sublabel={periodRange}
        icon={DollarSign}
      />
      <KpiCard
        label="Active agents"
        value={agentCountLabel}
        sublabel="configured"
        icon={Bot}
      />
      <KpiCard
        label="Conversations"
        value={conversations24hLabel}
        sublabel="last 24 hours"
        icon={MessagesSquare}
      />
    </div>
  );
}

// ---------- Skeleton (initial paint) -------------------------------------

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="h-full">
            <CardContent className="p-4 sm:p-5">
              <Skeleton className="h-9 w-9 rounded-md sm:h-10 sm:w-10" />
              <Skeleton className="mt-3 h-3 w-16" />
              <Skeleton className="mt-1 h-8 w-20 sm:h-9 sm:w-24" />
              <Skeleton className="mt-1 h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <Card className="lg:col-span-3">
          <CardHeader className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            <Skeleton className="h-28 w-28 rounded-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------- Plan-mismatch banner -----------------------------------------
// Shown above the returning-tenant dashboard when at least one agent is
// configured with a model not in the current plan's allowed_models.
// Typical trigger: a tenant downgraded from Pro → Free/Starter and left
// agents pinned to gpt-4o (or similar). The chat-time gate on
// /v1/chat/stream rejects those requests with `model_not_in_plan`; this
// banner surfaces the queue so the operator can fix or upgrade before
// users hit the wall.

function PlanMismatchBanner({ count }: { count: number }) {
  if (count <= 0) return null;
  const heading =
    count === 1 ? '1 agent needs attention' : `${count.toLocaleString()} agents need attention`;
  const body =
    count === 1
      ? 'An agent is configured with a model that is not available on your current plan. Edit it to pick a permitted model, or upgrade.'
      : 'Some agents are configured with models that are not available on your current plan. Edit them to pick permitted models, or upgrade.';
  return (
    <div
      role="alert"
      className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">{heading}</p>
          <p className="text-muted-foreground">{body}</p>
          <div className="pt-1">
            <Link to="/agents" className="underline-offset-2 hover:underline">
              Review agents →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Welcome (fresh-tenant) checklist -----------------------------

function WelcomeChecklist() {
  const api = useApiClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const tryDemo = useMutation({
    mutationFn: () => agentsApi.createSample(api),
    onSuccess: ({ agentId }) => {
      setError(null);
      // Invalidate the agents list so a back-nav reflects the new entry.
      void queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      // Land on the playground tab so the user sees the widget immediately.
      navigate(`/agents/${agentId}?tab=playground`);
    },
    onError: (err) => {
      setError(friendlyError(err, 'Could not create demo agent.'));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" aria-hidden />
          Get started in 3 steps
        </CardTitle>
        <CardDescription>
          Cortex creates a working assistant for you in one click — no setup needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChecklistStep
          n={1}
          icon={<Wand2 className="h-4 w-4" aria-hidden />}
          title="Create a sample assistant"
          subtitle="We'll spin up a demo agent with a small knowledge base so you can see it in action right away."
          action={
            <Button
              onClick={() => { tryDemo.mutate(); }}
              disabled={tryDemo.isPending}
              aria-label="Create sample assistant"
            >
              {tryDemo.isPending ? 'Preparing your assistant…' : 'Try a sample assistant'}
            </Button>
          }
        />
        <ChecklistStep
          n={2}
          icon={<Rocket className="h-4 w-4" aria-hidden />}
          title="Test it in the playground"
          subtitle="Ask the assistant a question and watch it answer in real time — same widget your visitors will see."
        />
        <ChecklistStep
          n={3}
          icon={<Check className="h-4 w-4" aria-hidden />}
          title="Embed on your website"
          subtitle="Copy a single line of HTML and paste it into your site. The chat bubble appears for your visitors."
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ChecklistStep({
  n,
  icon,
  title,
  subtitle,
  action,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border bg-muted/40 p-4">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold tabular-nums text-primary">
        {n}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          {icon}
          {title}
        </div>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
        {action ? <div className="pt-2">{action}</div> : null}
      </div>
    </div>
  );
}
